import {
  RECENTLY_VISITED_PAGES_KEY,
  RECENT_SEARCH_KEY,
  SEARCH_X_SEARCH_MODAL_ALLOWED_PAGES,
  excludedRoutesPathForGlobalSearch
} from "@/src/common/business-layer/constants"
import { SearchResultList } from "@/src/common/components/GlobalSearchModal/SearchResult"
import { RecentSearchItem } from "@/src/common/types"
import { useAnalytics } from "@/src/sb/components/Analytics"
import { ENABLE_SEARCH_V2 } from "@/src/sb/constants/featureFlags"
import { useUser } from "@/src/sb/hooks/useUser"
import { useFeatureValue } from "@growthbook/growthbook-react"
import { useRouter } from "next/router"
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState
} from "react"
import { useHotkeys } from "react-hotkeys-hook"

type GlobalSearchContextType = {
  isSearchOpen: boolean
  openSearch: () => void
  closeSearch: () => void
  onFilterToggle?: (e: React.MouseEvent<HTMLButtonElement>) => void
  setOnFilterToggle?: (
    fn: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined
  ) => void
  showFilterOnMobile: boolean
  onCloseFilters: () => void
  onOpenFilters: () => void
  lastVisitedUrl: string
  setLastVisitedUrl: (url: string) => void
  searchTerm?: string
  setSearchTerm: (url: string) => void
  sharedQuery: string
  setSharedQuery: (query: string) => void
  sharedAiMode: boolean | null
  setSharedAiMode: (aiMode: boolean | null) => void
  recentlyVisitedPages: SearchResultList[]
  addRecentlyVisitedPage: (page: SearchResultList, userId: string) => void
  recentSearches: { name: string }[]
  addRecentSearch: (search: RecentSearchItem, userId: string) => void
  removeRecentSearch: (search: RecentSearchItem) => void
}

const GlobalSearchContext = createContext<GlobalSearchContextType | undefined>(
  undefined
)

export const GlobalSearchProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter()
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [showFilterOnMobile, setShowFilterOnMobile] = useState(false)
  const [lastVisitedUrl, setLastVisitedUrl] = useState<string>("")
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [sharedQuery, setSharedQuery] = useState<string>("")
  const [sharedAiMode, setSharedAiMode] = useState<boolean | null>(null)
  const [recentSearches, setRecentSearches] = useState<{ name: string }[]>([])
  const [recentlyVisitedPages, setRecentlyVisitedPages] = useState<
    SearchResultList[]
  >([])

  const openSearch = () => setIsSearchOpen(true)
  const closeSearch = () => setIsSearchOpen(false)

  const useSearchV2 = useFeatureValue(ENABLE_SEARCH_V2, false)

  const [onFilterToggle, setOnFilterToggle] = useState<
    ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined
  >()

  const addRecentlyVisitedPage = (
    newPage: SearchResultList,
    userId: string
  ) => {
    const { slug, type, name } = newPage
    if (!slug || !type || !name || !userId.trim()) return

    if (!SEARCH_X_SEARCH_MODAL_ALLOWED_PAGES.includes(type)) return

    const userSpecificKey = `${RECENTLY_VISITED_PAGES_KEY}_${userId}`

    setRecentlyVisitedPages((prevPages) => {
      const updatedPages = [newPage, ...prevPages]
      const alreadyVisitedPages = new Set<string>()
      const updatedPage: SearchResultList[] = []

      for (const page of updatedPages) {
        if (!alreadyVisitedPages.has(page?.name as string)) {
          alreadyVisitedPages.add(page?.name as string)
          updatedPage.push(page)
        }
        if (updatedPage.length === 5) break
      }

      localStorage.setItem(userSpecificKey, JSON.stringify(updatedPage))

      return updatedPage
    })
  }

  const addRecentSearch = (search: { name: string }, userId: string) => {
    if (!search.name.trim() || !userId.trim()) return

    const userSpecificKey = `${RECENT_SEARCH_KEY}_${userId}`

    setRecentSearches((prev) => {
      const alreadyAdded = prev.find((s) => s.name === search.name)
      if (alreadyAdded) return prev
      const updatedSearch = [search, ...prev].slice(0, 6)
      localStorage.setItem(userSpecificKey, JSON.stringify(updatedSearch))
      return updatedSearch
    })
  }

  const removeRecentSearch = (searchToRemove: { name: string }) => {
    const updated = recentSearches.filter((s) => s.name !== searchToRemove.name)
    setRecentSearches(updated)
    localStorage.setItem(
      `${RECENT_SEARCH_KEY}_${userId}`,
      JSON.stringify(updated)
    )
  }

  const onOpenFilters = () => setShowFilterOnMobile(true)
  const onCloseFilters = () => setShowFilterOnMobile(false)
  const analytics = useAnalytics()

  const user = useUser()
  const userId = user?.data?.user_id?.toString() || ""

  useEffect(() => {
    if (userId) {
      const storedSearches = localStorage.getItem(
        `${RECENT_SEARCH_KEY}_${userId}`
      )
      setRecentSearches(storedSearches ? JSON.parse(storedSearches) : [])
      const storedPages = localStorage.getItem(
        `${RECENTLY_VISITED_PAGES_KEY}_${userId}`
      )
      setRecentlyVisitedPages(storedPages ? JSON.parse(storedPages) : [])
    }
  }, [userId])

  const isLicenceHolder = user?.userType === "subscriber"

  const isMac =
    typeof window !== "undefined" &&
    navigator.userAgent.toUpperCase().includes("MAC")
  const hotkey = isMac ? "meta+k" : "ctrl+k"

  useHotkeys(hotkey, (event) => {
    if (
      !excludedRoutesPathForGlobalSearch.includes(router.pathname) &&
      useSearchV2 &&
      isLicenceHolder
    ) {
      event.preventDefault()
      openSearch()
      analytics.startGlobalSearch()
    }
  })

  useHotkeys(
    "esc",
    (event) => {
      if (!excludedRoutesPathForGlobalSearch.includes(router.pathname)) {
        event.preventDefault()
        closeSearch()
        analytics.abandonGlobalSearch(searchTerm)
      }
    },
    {
      enableOnFormTags: true
    }
  )

  return (
    <GlobalSearchContext.Provider
      value={{
        isSearchOpen,
        openSearch,
        closeSearch,
        onFilterToggle,
        setOnFilterToggle,
        showFilterOnMobile,
        onCloseFilters,
        onOpenFilters,
        lastVisitedUrl,
        setLastVisitedUrl,
        searchTerm,
        setSearchTerm,
        sharedQuery,
        setSharedQuery,
        sharedAiMode,
        setSharedAiMode,
        recentlyVisitedPages,
        addRecentlyVisitedPage,
        recentSearches,
        addRecentSearch,
        removeRecentSearch
      }}
    >
      {children}
    </GlobalSearchContext.Provider>
  )
}

export const useGlobalSearch = () => {
  const context = useContext(GlobalSearchContext)
  if (!context) {
    throw new Error("must be used within a GlobalSearchProvider")
  }
  return context
}
